import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { useAtom } from 'jotai';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, RefreshCw, Check, Loader2, AlertCircle, History, Download, Trash2, FilePen,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Badge, Button, Separator,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@higgins/ui';
import { SectionTabs, type AllSectionContent } from '@/components/report-editor/section-tabs';
import { VersionPanel } from '@/components/version-panel';
import { reportsAtom, versionPanelOpenAtom } from '@/store/atoms';
import { useReport, useSaveReport, usePublishReport, useExportReport, useDeleteReport, useReportVisuals, useAmendReport } from '@/lib/hooks';
import { refreshVisuals } from '@/lib/api';
import { SECTION_KEYS, MONTHS } from '@/lib/types';
import type { ReportStatus, SectionKey } from '@/lib/types';

const IS_MOCK = !import.meta.env.VITE_API_BASE_URL;

// Visuals captured longer ago than this prompt the user to refresh on open.
const STALENESS_THRESHOLD_MS = 24 * 60 * 60 * 1000;

const STATUS_BADGE: Record<ReportStatus, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
  Draft:       { label: 'Draft',       variant: 'secondary' },
  Published:   { label: 'Published',   variant: 'default'   },
  Amended:     { label: 'Amended',     variant: 'outline'   },
  Distributed: { label: 'Distributed', variant: 'default'   },
};

function emptySection() {
  return { content: '', content2: '', content3: '' };
}

function initialSections(): AllSectionContent {
  return Object.fromEntries(SECTION_KEYS.map(k => [k, emptySection()])) as AllSectionContent;
}

// Merge two pending-edit batches per section+field; `newer` wins on conflict. Used to
// restore a failed save's batch without clobbering edits made during the round-trip.
function mergePending(older: AllSectionContent, newer: AllSectionContent): AllSectionContent {
  const merged: AllSectionContent = { ...older };
  for (const key of Object.keys(newer) as SectionKey[]) {
    merged[key] = { ...(older[key] ?? {}), ...(newer[key] ?? {}) } as AllSectionContent[SectionKey];
  }
  return merged;
}

function formatCapturedOn(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

export default function ReportPage() {
  const { id } = useParams<{ id: string }>();
  const navigate  = useNavigate();
  const qc        = useQueryClient();

  // Mock-mode state
  const [mockReports, setMockReports] = useAtom(reportsAtom);
  const mockReport = mockReports.find(r => r.id === id);

  // API-mode hooks
  const { data: apiData, isLoading: reportLoading } = useReport(id!);
  const { data: visualsData, isLoading: visualsLoading } = useReportVisuals(id!);

  // Resolved report
  const report = IS_MOCK ? mockReport : (apiData ?? mockReport);

  const [saveStatus, setSaveStatusLocal] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [versionPanelOpen, setVersionPanelOpen] = useAtom(versionPanelOpenAtom);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [deleteDialogOpen,  setDeleteDialogOpen]  = useState(false);

  const [sections, setSections] = useState<AllSectionContent>(initialSections);

  // Hydrate the editor from the server ONCE per report. Re-running on every apiData
  // refetch (e.g. after an autosave invalidates the query) would overwrite in-progress
  // edits with the just-saved server copy and wipe recent keystrokes/formatting (#93).
  const hydratedIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (IS_MOCK || !id) return;
    if (hydratedIdRef.current === id) return;   // already hydrated this report
    if (!apiData?.sections?.length) return;      // wait for the real sections to arrive
    const loaded = Object.fromEntries(
      apiData.sections.map(s => [
        s.sectionKey,
        { content: s.content, content2: s.content2, content3: s.content3 },
      ])
    ) as AllSectionContent;
    setSections(loaded);
    hydratedIdRef.current = id;
  }, [apiData, id]);

  // API mutations
  const saveMutation    = useSaveReport(id!);
  const publishMutation = usePublishReport(id!);
  const deleteMutation  = useDeleteReport();
  const exportMutation  = useExportReport();
  const amendMutation   = useAmendReport();

  const [exportingFormat, setExportingFormat] = useState<'word' | 'pptx' | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  async function handleExport(format: 'word' | 'pptx') {
    if (!id || !report) return;
    setExportingFormat(format);
    const month = MONTHS[report.month - 1];
    const scope = report.branches.length > 0 && report.branches.length <= 3
      ? report.branches.join(', ')
      : report.region;
    const filename = `GM Report - ${scope} - ${month} ${report.year}`;
    try {
      await exportMutation.mutateAsync({ id, format, filename });
      toast.success('Export ready', { description: `Your ${format === 'word' ? 'Word' : 'PowerPoint'} document is downloading.` });
    } catch (e: any) {
      const description = e?.message?.startsWith('No archived')
        ? e.message
        : 'Please try again. If the problem persists, contact support.';
      toast.error('Export failed', { description });
    } finally {
      setExportingFormat(null);
    }
  }

  // Auto-save
  const isDirty         = useRef(false);
  const saveTimerRef    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTimerRef   = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSections = useRef<AllSectionContent>({});
  const saveMutationRef = useRef(saveMutation);
  saveMutationRef.current = saveMutation;

  const save = useCallback(async () => {
    if (!isDirty.current) return;
    setSaveStatusLocal('saving');

    // Snapshot what we're about to save, then reset the accumulator + dirty flag so any
    // edits made DURING the round-trip land in a fresh batch instead of being wiped when
    // this save resolves (#93). handleContentChange re-sets isDirty + schedules the next
    // autosave for those, so they're not lost.
    const snapshot = pendingSections.current;
    pendingSections.current = {};
    isDirty.current = false;

    try {
      if (!IS_MOCK) {
        const sectionPayload = SECTION_KEYS
          .filter(k => snapshot[k])
          .map(k => ({ sectionKey: k, ...(snapshot[k] ?? {}) }));
        await saveMutationRef.current.mutateAsync({ sections: sectionPayload });
      } else {
        await new Promise(res => setTimeout(res, 400));
      }
      setSaveStatusLocal('saved');
      if (!IS_MOCK && id) {
        setMockReports(prev =>
          prev.map(r => r.id === id ? { ...r, modifiedOn: new Date().toISOString() } : r)
        );
      }
      savedTimerRef.current = setTimeout(() => {
        setSaveStatusLocal('idle');
      }, 3000);
    } catch {
      // Restore the failed batch so it retries, without clobbering edits made during the
      // await (those win per field).
      pendingSections.current = mergePending(snapshot, pendingSections.current);
      isDirty.current = true;
      setSaveStatusLocal('error');
      toast.error('Auto-save failed', { description: 'Changes not saved. Please try again.' });
    }
  }, [id, setMockReports]);

  function scheduleAutoSave() {
    isDirty.current = true;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(save, 3_000);
  }

  useEffect(() => {
    return () => {
      if (saveTimerRef.current)  clearTimeout(saveTimerRef.current);
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      if (isDirty.current) save();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [save]);

  function handleContentChange(
    sectionKey: SectionKey,
    field: 'content' | 'content2' | 'content3',
    value: string,
  ) {
    setSections(prev => ({
      ...prev,
      [sectionKey]: { ...prev[sectionKey], [field]: value },
    }));
    pendingSections.current = {
      ...pendingSections.current,
      [sectionKey]: { ...(pendingSections.current[sectionKey] ?? {}), [field]: value },
    };
    scheduleAutoSave();
  }

  function handleTabChange() {
    if (isDirty.current) {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      save();
    }
  }

  const handleRefreshVisuals = useCallback(async () => {
    if (!id) return;
    if (IS_MOCK) {
      qc.invalidateQueries({ queryKey: ['report-visuals'] });
      toast.info('Visuals refreshing…');
      return;
    }
    setIsRefreshing(true);
    try {
      const res = await refreshVisuals(id);
      // Snapshot updated server-side — re-fetch the on-screen visuals.
      qc.invalidateQueries({ queryKey: ['report-visuals', id] });
      if (res.failedCount > 0) {
        const failed = res.results.filter(r => !r.ok).map(r => `${r.section}/${r.slot}`).join(', ');
        toast.warning(`Refreshed with ${res.failedCount} failure(s)`, {
          description: `${res.total - res.failedCount}/${res.total} captured. Failed: ${failed}`,
        });
      } else {
        toast.success('Visuals refreshed', { description: `${res.total} visuals captured.` });
      }
    } catch (e: any) {
      toast.error('Refresh failed', { description: e.message });
    } finally {
      setIsRefreshing(false);
    }
  }, [id, qc]);

  // On open: auto-refresh when no snapshot exists yet (new/cloned report), or
  // prompt — never silently refresh — when the snapshot is older than the
  // staleness threshold. Editable drafts only; published reports trust their
  // locked snapshot. Runs once per report id.
  const staleCheckedRef = useRef<string | null>(null);
  useEffect(() => {
    if (IS_MOCK || !id || !visualsData || !report) return;
    if (report.status !== 'Draft') return;
    if (staleCheckedRef.current === id) return;
    staleCheckedRef.current = id;

    if (visualsData.capturedOn === null) {
      toast.info('Fetching visuals…');
      handleRefreshVisuals();
      return;
    }
    const age = Date.now() - new Date(visualsData.capturedOn).getTime();
    if (age > STALENESS_THRESHOLD_MS) {
      toast.warning('Visuals may be out of date', {
        description: `Last captured ${formatCapturedOn(visualsData.capturedOn)}.`,
        action: { label: 'Refresh', onClick: () => handleRefreshVisuals() },
        duration: 10_000,
      });
    }
  }, [id, visualsData, report, handleRefreshVisuals]);

  async function handleAmend() {
    if (!id) return;
    if (IS_MOCK) {
      toast.info('Amend is available once the API is connected.');
      return;
    }
    try {
      const { id: newId } = await amendMutation.mutateAsync(id);
      toast.success('Editable draft created', { description: 'Correct and re-publish as needed.' });
      navigate(`/reports/${newId}`);
    } catch (e: any) {
      toast.error('Failed to create draft', { description: e.message });
    }
  }

  async function handlePublish() {
    if (!report) return;
    if (IS_MOCK) {
      setMockReports(prev =>
        prev.map(r => r.id === id
          ? { ...r, status: 'Published', publishedOn: new Date().toISOString() }
          : r
        )
      );
      toast.success('Report published', { description: `${report.monthYear} is now read-only.` });
    } else {
      try {
        const result = await publishMutation.mutateAsync();
        if (result.warnings.length) {
          toast.warning('Published with warnings', { description: result.warnings.join(', ') });
        } else {
          toast.success('Report published', { description: `${report.monthYear} is now read-only.` });
        }
      } catch (e: any) {
        toast.error('Publish failed', { description: e.message });
      }
    }
    setPublishDialogOpen(false);
  }

  async function handleDelete() {
    if (!id || !report) return;
    if (IS_MOCK) {
      setMockReports(prev => prev.filter(r => r.id !== id));
      toast.success('Report deleted');
      navigate('/');
    } else {
      try {
        await deleteMutation.mutateAsync(id);
        toast.success('Report deleted');
        navigate('/');
      } catch (e: any) {
        toast.error('Failed to delete report', { description: e.message });
      }
    }
    setDeleteDialogOpen(false);
  }

  if (!report) {
    // First load: the report exists but its fetch is still in flight. Show a
    // spinner rather than the not-found screen, which would otherwise flash for
    // a second or two on a freshly created report before the query resolves.
    if (!IS_MOCK && reportLoading) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-3 text-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading report…</p>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 text-center">
        <p className="font-semibold text-slate-900">Report not found</p>
        <p className="text-sm text-muted-foreground">
          The report ID <code className="text-xs bg-muted px-1 rounded">{id}</code> doesn't exist.
        </p>
        <Button variant="outline" onClick={() => navigate('/')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Reports
        </Button>
      </div>
    );
  }

  const { label, variant } = STATUS_BADGE[report.status];
  // Locked, non-editable statuses. Amended is a retired former-published version.
  const isReadOnly = report.status === 'Published'
    || report.status === 'Amended'
    || report.status === 'Distributed';

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="space-y-5"
      >
        {/* Header */}
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              Reports
            </Button>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-slate-900">
                {MONTHS[report.month - 1]} {report.year}
              </h2>
              <Badge variant={variant}>{label}</Badge>
            </div>

            {/* Save status */}
            <div className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground min-w-[80px] justify-end">
              {saveStatus === 'saving' && <><Loader2 className="w-3 h-3 animate-spin" />Saving…</>}
              {saveStatus === 'saved'  && <><Check className="w-3 h-3 text-green-500" />Saved</>}
              {saveStatus === 'error'  && <><AlertCircle className="w-3 h-3 text-destructive" />Save failed</>}
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 flex-wrap">
            <p className="text-sm text-muted-foreground space-x-1">
              <span className="font-medium text-foreground">{report.region}</span>
              <span>·</span>
              <span>{report.branches.join(', ')}</span>
              <span>·</span>
              <span>{report.divisions.join(', ')}</span>
              {report.preparedBy && (
                <><span>·</span><span>Prepared by {report.preparedBy}</span></>
              )}
            </p>

            <div className="flex items-center gap-2">
              {visualsData?.capturedOn && (
                <span className="text-xs text-muted-foreground mr-1" title="When the report's visual data was last captured">
                  Data as of {formatCapturedOn(visualsData.capturedOn)}
                </span>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setVersionPanelOpen(v => !v)}
                className={versionPanelOpen ? 'bg-muted' : ''}
              >
                <History className="w-3.5 h-3.5 mr-1.5" />
                History
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('word')}
                disabled={IS_MOCK || exportingFormat !== null}
                title="Download Word document"
              >
                {exportingFormat === 'word'
                  ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  : <Download className="w-3.5 h-3.5 mr-1.5" />}
                Word
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('pptx')}
                disabled={IS_MOCK || exportingFormat !== null}
                title="Download PowerPoint"
              >
                {exportingFormat === 'pptx'
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Download className="w-3.5 h-3.5" />}
                <span className="ml-1.5">PPTX</span>
              </Button>

              {report.status === 'Published' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAmend}
                  disabled={IS_MOCK || amendMutation.isPending}
                  title="Create an editable amended copy of this report"
                >
                  {amendMutation.isPending
                    ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    : <FilePen className="w-3.5 h-3.5 mr-1.5" />}
                  Amend
                </Button>
              )}

              {!isReadOnly && (
                <>
                  <Button variant="outline" size="sm" onClick={handleRefreshVisuals} disabled={isRefreshing}>
                    {isRefreshing
                      ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                    Refresh
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteDialogOpen(true)}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    title="Delete draft"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                    Delete
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setPublishDialogOpen(true)}
                    disabled={publishMutation.isPending}
                  >
                    Publish
                  </Button>
                </>
              )}
            </div>
          </div>

          <Separator />
        </div>

        {/* Section editor */}
        <SectionTabs
          sections={sections}
          visuals={visualsData?.visuals}
          visualsLoading={visualsLoading || isRefreshing}
          readOnly={isReadOnly}
          onContentChange={handleContentChange}
          onTabChange={handleTabChange}
        />
      </motion.div>

      <VersionPanel
        reportId={id!}
        open={versionPanelOpen}
        onClose={() => setVersionPanelOpen(false)}
      />

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Draft</DialogTitle>
            <DialogDescription>
              Permanently delete <strong>{MONTHS[report.month - 1]} {report.year}</strong>? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleteMutation.isPending}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending
                ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                : <Trash2 className="w-3.5 h-3.5 mr-2" />
              }
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Publish report?</DialogTitle>
            <DialogDescription>
              This will make <strong>{MONTHS[report.month - 1]} {report.year}</strong> read-only.
              Published reports cannot be edited. Exports will remain available.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPublishDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handlePublish} disabled={publishMutation.isPending}>
              {publishMutation.isPending && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />}
              Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
